import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ProblemSection = () => {
  const problems = [
    {
      icon: "Clock",
      title: "Manual List Building is Slow",
      description: "Sales reps spend 40% of their week pulling data from intent databases instead of actually selling.",
      stat: "21 hrs",
      statLabel: "per week on non-selling tasks"
    },
    {
      icon: "TrendingDown",
      title: "Generic Outreach Fails",
      description: "Mass, unpersonalized sequence emails end up in spam and damage your domain reputation.",
      stat: "1.7%",
      statLabel: "average generic cold email reply rate"
    },
    {
      icon: "DollarSign",
      title: "Scaling Outbound is Expensive",
      description: "Growing your SDR team means skyrocketing payroll, software seats, and management overhead.",
      stat: "$85K+",
      statLabel: "average SDR loaded cost"
    }
  ];

  return (
    <section id="problem" className="py-20 bg-surface">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-headline-bold text-3xl md:text-5xl text-foreground mb-6">
            The Outbound Growth Problem
          </h2>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
            Building high-quality lead lists and writing personalized emails manually takes hours.
            Meanwhile, spray-and-pray automated sequences ruin domain reputation and get zero replies.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="glassmorphism rounded-xl p-8 text-center hover:scale-105 transition-transform duration-300"
            >
              <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name={problem.icon} size={32} color="var(--color-error)" />
              </div>

              <h3 className="font-headline-bold text-xl text-foreground mb-4">
                {problem.title}
              </h3>

              <p className="font-body text-muted-foreground mb-6 leading-relaxed">
                {problem.description}
              </p>

              <div className="border-t border-border pt-6">
                <div className="text-3xl font-headline-black text-error mb-2">
                  {problem.stat}
                </div>
                <div className="text-sm font-body-medium text-muted-foreground">
                  {problem.statLabel}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pain Point Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="glassmorphism rounded-xl p-8 max-w-4xl mx-auto">
            <Icon name="Quote" size={48} color="var(--color-primary)" className="mx-auto mb-6 opacity-50" />
            <blockquote className="font-body text-xl md:text-2xl text-foreground mb-6 italic">
              "I spend half my day building lists and cleaning CSVs.
              By the time I sit down to write personalized emails for top accounts, my day is gone."
            </blockquote>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                <Icon name="User" size={24} color="var(--color-muted-foreground)" />
              </div>
              <div className="text-left">
                <div className="font-body-semibold text-foreground">Sarah Chen</div>
                <div className="text-sm text-muted-foreground">Founding Account Executive, B2B SaaS Inc.</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;